import type { GlobeView, PlanetGlobeHandle } from "./globeTypes";
import { MAX_VIEW_LAT, projectToScreen } from "../planet3d/globeModel";
import { mergedKeys } from "./publicMapModel";
import { PAN_MS, type MapCamera } from "@/shared/map/mapCamera";

/**
 * 3D 星球上的就近面板（UI-WORLD-LIVING-117-2b 评审 R1 高 1：3D 上点不是页面元素，量不到，卡照旧贴底就会盖住被点的那一位）。
 * 星球的句柄（6796 的 PlanetGlobe，10-16 同意加的三个透传）给出此刻屏幕上的光点（screenGroups，画布容器里的 CSS 像素，和页面比要加上容器的 top）和镜头（getView、setView）：
 * - globePinRect、globePinHit：被点的那一处在页面上的框、按下的地方落没落在它上面（光点圆心、半径；screenGroups 的半径已含 6px 留边，这里不再加），和 2D 量标记同一个口径；
 * - globeRepKey：屏幕上那一团是按哪一格画的（screenGroups 取团里人最多的那一格的位置）。转球按这一格算，放置量的也是它（评审 R5 高：小屏访客从合成那一团的名单里选西安居民，
 *   转球按别的格算，能挪的量算小了，卡落成 bare、照片撤掉）；
 * - globeCamera：把被点的那一位在屏幕上往上挪——用 globeModel 同一套投影（projectToScreen）解出镜头转到哪，按帧转过去（PAN_MS，和 2D 挪地图一样快），
 *   减少动态时一步到位。经度转到那一位正上方（让 TA 落在中轴上），纬度往南转把 TA 抬上去；远近不动。
 *   只转纬度时，偏在一边的那一位往上走得很少（评审 R3 高 1：小屏上偏心的光点挪不动，照片被撤），经度转正以后能挪的和居中时一样多。
 *   球能转的有限（评审 R2 阻断：那一位转到球背面、或镜头纬度到了上限 MAX_VIEW_LAT 就上不去了）：reach() 先算出最多能往上挪多少，放置只按这个量排档；
 *   要挪得比这多时挪到头。
 *   keep：被选中那位自己那一格（和画那一团的那一格不是一格时给）。转的时候它要留在看得见的半边，要转到背面就停在那之前，能挪的少一点、放置收一档
 *   （评审 R7 中 1：点名要看西安的那位，球转到了香港，TA 转到了地平线后面）。
 *   restore 把球转回去（评审 R2 中：关卡后球斜着停住；什么时候转回由地图定）；转完以后用户自己拖过、缩放过球就不动 TA 的。
 * 句柄没有这几个方法、还没加载完、远景和穿梭时（没有光点；6796 提醒这时 setView 只改落地目标、画面不动）都量不到，返回 null，地图照旧贴底、不转。
 */
type Globe = Pick<PlanetGlobeHandle, "screenGroups" | "getView" | "setView">;
export type GlobeCamera = MapCamera & {
  reach: () => number;
  /** 转之前的镜头 */
  start: GlobeView;
  /** 此刻还停在转出来的那条路上（转过、用户后来没动过球；转到一半也算）：地图据此决定接着记还是重新记（评审 R4 中 C） */
  holds: () => boolean;
  /** 转回去（还在转就先停）：转到 back（默认转之前的镜头）。没转过、或用户后来自己动过球，不转、回 null；转了回转到的那个镜头 */
  restore: (animate: boolean, back?: GlobeView) => GlobeView | null;
};

function groupOf(globe: Globe, key: string) {
  const want = new Set(mergedKeys(key));
  return (globe.screenGroups?.() ?? []).find((group) => group.keys.some((k) => want.has(k))) ?? null;
}

/** 被点的那一处在页面上的框（host 是星球的画布容器），量不到为 null */
export function globePinRect(globe: Globe | null, key: string, host: Element | null): { top: number; bottom: number; height: number } | null {
  if (!globe || !host) return null;
  const group = groupOf(globe, key);
  if (!group) return null;
  const box = host.getBoundingClientRect();
  return { top: box.top + group.y - group.radius, bottom: box.top + group.y + group.radius, height: 2 * group.radius };
}

/**
 * 页面上 (x, y) 这一下落没落在被点的那一处上（3D 上「再点同一处」：评审 R3 中 1）。
 * 转完球那一团可能分成几盏（评审 R8 中：390 上从港深、西安合成的那一团名单里选了西安的那位，转完分成港深、西安两盏），含那几格的哪一盏都算
 */
export function globePinHit(globe: Globe | null, key: string, host: Element | null, x: number, y: number): boolean {
  if (!globe || !host) return false;
  const want = new Set(mergedKeys(key));
  const box = host.getBoundingClientRect();
  return (globe.screenGroups?.() ?? []).some((group) => group.keys.some((k) => want.has(k)) && Math.hypot(x - (box.left + group.x), y - (box.top + group.y)) <= group.radius);
}

/** 屏幕上那一团是按哪一格画的（那一格的 key）；量不到为 null */
export function globeRepKey(globe: Globe | null, key: string): string | null {
  return globe ? groupOf(globe, key)?.key ?? null : null;
}

/**
 * 那一位在球背面时，先一步把 TA 转到看得见的地方（经度对正、TA 在视心南边 12 度），返回转之前的镜头；本来就看得见、量不了时返回 null。
 * 评审 R9 高：从主页回来地图重新挂上，转过的记录丢了，回到名单不转回；再换一位，这一位在地平线后面，卡量不到 TA、整张贴底盖住球。新鲜事里点开背面那一位也走这条
 */
export function globeBringIntoView(globe: Globe | null, at: { lat: number; lng: number }, host: Element | null): GlobeView | null {
  if (!globe?.getView || !globe.setView || !host) return null;
  const view = globe.getView();
  const { width, height } = host.getBoundingClientRect();
  if (!view || !width || !height || projectToScreen(at.lat, at.lng, view, width, height).front) return null;
  globe.setView({ ...view, lat: Math.max(-MAX_VIEW_LAT, Math.min(MAX_VIEW_LAT, at.lat + 12)), lng: at.lng }, false);
  return view;
}

/** 经度从 from 到 to 走近的那一边的差（-180～180） */
const lngDelta = (from: number, to: number) => ((((to - from) % 360) + 540) % 360) - 180;

/**
 * 把被点的那一处（at：屏幕上画那一团的那一格的经纬度，见 globeRepKey）在屏幕上往上挪的镜头口子；dx 不管，dy 只认往上（负数）。
 * 转到位以后把最终镜头交给 onSettled（地图存进回来接着看的那一份）：setView(…, false) 不走 globeStage 的 settle、不回调 onViewChange（6796 提醒），
 * 不补这一下，回来时球先回到转之前的朝向。
 */
export function globeCamera(globe: Globe | null, at: { lat: number; lng: number }, host: Element | null, onSettled?: (view: GlobeView) => void, keep?: { lat: number; lng: number }): GlobeCamera | null {
  if (!globe?.getView || !globe.setView || !host) return null;
  const start = globe.getView();
  if (!start) return null;
  const setView = globe.setView.bind(globe);
  const getView = globe.getView.bind(globe);
  // 经度转到那一位正上方
  const dLng = lngDelta(start.lng, at.lng);
  const viewAt = (lat: number): GlobeView => ({ ...start, lat, lng: start.lng + dLng });
  /**
   * 经度转正以后，镜头纬度是 lat 时那一位在屏幕上的 y；转到球背面为 null。
   * 那一位在中轴上，跟着纬度转是沿一条大圆走：看得见的半边上屏幕高度是单调的，往上走的极值正好在地平线背后
   * （globeModel.projectToScreen 的 front 按地平线算），所以一路试到看不见或纬度上限就是最高处；下面二分也靠这一点。
   */
  const measure = () => {
    const { width, height } = host.getBoundingClientRect();
    if (!width || !height) return null;
    const now = projectToScreen(at.lat, at.lng, start, width, height);
    if (!now.front) return null;
    const y = (lat: number) => { const p = projectToScreen(at.lat, at.lng, viewAt(lat), width, height); return p.front ? p.y : null; };
    // keep 那一格在这个镜头下还看不看得见（没给 keep 就不管）
    const kept = (lat: number) => !keep || projectToScreen(keep.lat, keep.lng, viewAt(lat), width, height).front;
    if (!kept(start.lat)) return null;
    const y1 = y(start.lat), y2 = y(start.lat + 1);
    if (y1 === null || y2 === null || y1 === y2) return null;
    // up：纬度往哪边转那一位往上走
    const up = y2 < y1 ? 1 : -1;
    let best = { lat: start.lat, y: y1 };
    for (let step = 1; step <= 2 * MAX_VIEW_LAT; step++) {
      const lat = start.lat + up * step;
      if (Math.abs(lat) > MAX_VIEW_LAT) break;
      const there = y(lat);
      if (there === null || !kept(lat)) break;
      best = { lat, y: there };
    }
    return { y0: now.y, best, up, y };
  };
  /** 转到的那个镜头（没转过是 null）；按帧转时的帧号 */
  let turnedTo: GlobeView | null = null;
  let frame = 0;
  /** 此刻还在「转之前 → 转到」那条路上（纬度、经度按同一个进度走，可能转到一半被停住）；出了这条路就是用户自己动过的 */
  const onPath = () => {
    const now = getView(), to = turnedTo;
    if (!to || !now || Math.abs(now.distance - start.distance) > 1e-4) return false;
    const wantLat = to.lat - start.lat, wantLng = lngDelta(start.lng, to.lng);
    const gotLat = now.lat - start.lat, gotLng = lngDelta(start.lng, now.lng);
    const k = Math.abs(wantLat) >= Math.abs(wantLng) ? (wantLat ? gotLat / wantLat : 0) : gotLng / wantLng;
    return k >= -0.01 && k <= 1.01 && Math.abs(gotLat - k * wantLat) < 0.01 && Math.abs(gotLng - k * wantLng) < 0.01;
  };
  return {
    start,
    holds: onPath,
    restore(animate, back = start) {
      if (frame) cancelAnimationFrame(frame);
      frame = 0;
      const ok = onPath();
      turnedTo = null;
      if (!ok) return null;
      setView(back, animate);
      return back;
    },
    reach() {
      const m = measure();
      return m ? Math.max(0, m.y0 - m.best.y) : 0;
    },
    panBy(_dx, dy, instant) {
      if (!(dy < 0)) return;
      const m = measure();
      if (!m) return;
      const target = Math.max(m.y0 + dy, m.best.y);
      if (m.y0 - target < 0.5) return;
      // 二分的另一头：从 start.lat 往「往下走」那边找到第一处不比目标高的（经度转正这一下可能已经抬过头，要往回转一点）
      let low = start.lat;
      for (let step = 0; step <= 2 * MAX_VIEW_LAT; step++) {
        const lat = start.lat - m.up * step;
        if (Math.abs(lat) > MAX_VIEW_LAT) break;
        const there = m.y(lat);
        if (there === null) break;
        low = lat;
        if (there >= target) break;
      }
      // y 从 low 到 best.lat 一路往上（变小）：二分到很小的角度
      let lo = low, hi = m.best.lat;
      for (let i = 0; i < 40 && Math.abs(hi - lo) > 1e-5; i++) {
        const mid = (lo + hi) / 2;
        const y = m.y(mid);
        if (y !== null && y > target) lo = mid; else hi = mid;
      }
      const to = viewAt(hi);
      turnedTo = to;
      if (instant || typeof requestAnimationFrame !== "function") { setView(to, false); onSettled?.(to); return; }
      let begin: number | null = null;
      const tick = (time: number) => {
        if (begin === null) begin = time;
        const k = Math.min(1, (time - begin) / PAN_MS);
        const eased = 1 - (1 - k) ** 3;
        setView({ ...start, lat: start.lat + (to.lat - start.lat) * eased, lng: start.lng + dLng * eased }, false);
        if (k < 1) frame = requestAnimationFrame(tick);
        else { frame = 0; onSettled?.(to); }
      };
      frame = requestAnimationFrame(tick);
    },
  };
}
